'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  onlineCount: number;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  onlineCount: 0,
});

export const useSocket = () => useContext(SocketContext);

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const { user, token } = useAuth();
  const userId = user?.id;
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);

  useEffect(() => {
    // Only connect if user is authenticated
    if (!userId) {
      return;
    }

    // Never hardcode a fallback host here — a stale fallback (e.g. a leftover third-party domain)
    // would silently send the user's JWT to the wrong server if env were misconfigured.
    const API_URL = process.env.NEXT_PUBLIC_API_URL || '';

    if (!API_URL) {
      console.error('[Socket] No backend URL configured; refusing to connect');
      return;
    }

    // If we don't have a token yet, wait for it
    if (!token) {
      return;
    }

    // Create socket connection. The connection is authenticated by the JWT (verified server-side)
    // plus the CORS origin allowlist — we do NOT send an API secret from the browser (it would be
    // inlined into the public bundle and authenticate nothing). See audit H15.
    const newSocket = io(API_URL, {
      auth: {
        token: token, // JWT access token
      },
      // WebSocket-only: the whole session rides one connection, so no per-session HTTP
      // request needs to return to the originating API replica. Keeping the 'polling'
      // fallback would require sticky sessions at the proxy (which Railway does not
      // guarantee); with >1 API replica a polling handshake split across replicas errors
      // with "Session ID unknown" and drops the chat connection. Trade-off: clients on a
      // proxy that blocks WebSockets fail to connect instead of degrading to polling.
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      withCredentials: true, // Send cookies with the request
    });

    // Connection events
    newSocket.on('connect', () => {
      setIsConnected(true);
    });

    newSocket.on('disconnect', (reason) => {
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error: any) => {
      setIsConnected(false);
    });

    newSocket.on('error', (error) => {
      // Silent error handling
    });

    // Online users count
    newSocket.on('users:online', (count: number) => {
      setOnlineCount(count);
    });

    // Handle reconnection
    newSocket.on('reconnect', () => {
      setIsConnected(true);
    });

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      newSocket.close();
      setSocket(null);
      setIsConnected(false);
    };
  }, [userId, token]); // Reconnect only when the authenticated identity or token changes

  const value = useMemo(() => ({ socket, isConnected, onlineCount }), [socket, isConnected, onlineCount]);

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}
